import { NetworkError } from '@/errors';
import type { IPayment } from '@/models/payments';
import { getOrderByIdWithPlans, getOrderByIdWithPlansAndPayments } from '@/services/orders';
import { useUserStore } from '@/stores/users';
import { useWidgetStore } from '@/stores/widget';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import type { RouteLocationNormalizedGeneric } from 'vue-router';
import { BASE_URL } from '../config';
import type { IPaymentPlan } from '../models/paymentPlans';
import { useMerchantStore } from '../stores/merchants';
import { getOrderLink } from './order.utils';

export const getRedirectUrl = (orderId: string, payment: IPayment) => {
  const widgetStore = useWidgetStore();
  let redirectUrl: URL;
  if (widgetStore.notifyUrl) {
    const notifyUrl = widgetStore.notifyUrl;
    redirectUrl = new URL(notifyUrl);

    redirectUrl.searchParams.append('orderId', orderId as string);
    redirectUrl.searchParams.append('success', payment.paid ? 'true' : 'false');
    redirectUrl.searchParams.append('paymentId', payment._id as string);
    redirectUrl.searchParams.append('pending', payment.pending ? 'true' : 'false');
  } else {
    redirectUrl = getOrderLink(orderId, widgetStore.order?.type);
  }

  return redirectUrl.href;
};

/**
 * Route guard that initializes and validates widget order access.
 *
 * This guard performs several key operations:
 * - Ensures merchants are signed out (widget flow is customer-only)
 * - Fetches the order with payment plans and validates order state
 * - Redirects completed/cancelled/expired orders to the purchases dashboard
 * - Routes partial orders appropriately based on authentication and order type
 * - Handles payment links and contribution flows
 * - Provides error handling with appropriate redirects for network/auth failures
 *
 * @param to - The target route location being navigated to
 * @returns Navigation redirect object or undefined to allow navigation
 * @throws Logs exceptions and network errors to monitoring
 */
export const initializeWidgetOrderGuard = async (to: RouteLocationNormalizedGeneric) => {
  const widgetStore = useWidgetStore();
  const userStore = useUserStore();

  const merchantStore = useMerchantStore();
  const id = to.params.id as string;
  try {
    // Merchants can't go through the widget flow.
    if (userStore.user && userStore.user.role === 'MERCHANT') {
      userStore.signOut();
      merchantStore.activeMerchantId = '';
    }

    const isRewardsPartialOrder = widgetStore.boost?.partialOrder?.type === 'REWARDS';
    if (!isRewardsPartialOrder) {
      // Reset store if the order ID in the store doesn't match the URL parameter
      if (widgetStore.order?._id && widgetStore.order._id !== id) {
        widgetStore.reset();
      }
      widgetStore.order =
        to.name === 'Contribute'
          ? await getOrderByIdWithPlansAndPayments(id as string)
          : await getOrderByIdWithPlans(id as string);
      addMetric('orders-getOrderByIdWithPlans', { status: 'success', channel: 'merchant' });
      widgetStore.plan = widgetStore.getPaymentPlan() as IPaymentPlan;
      if (
        widgetStore.order.state === 'COMPLETED' ||
        widgetStore.order.state === 'CANCELLED' ||
        widgetStore.order.state === 'EXPIRED'
      ) {
        window.location.href = getOrderLink(id, widgetStore.order.type).href;
        return;
      }
      const isPaymentLink =
        to.query.paymentMethod || to.query.amount || to.name === 'Contribute' || to.name === 'DebiCheck';

      // Used when returning from the payment breakdown on the contribution screen.
      if (to.name === 'Contribute' && widgetStore.order.type === 'REWARDS') {
        return;
      }
      if (
        (widgetStore.order.state !== 'PARTIAL' ||
          (widgetStore.order.type === 'REWARDS' && userStore.user.verifiedEmail)) &&
        !isPaymentLink
      ) {
        return {
          name: 'Contribute',
          params: { id },
        };
      } else if (widgetStore.order.state === 'PARTIAL' && to.name === 'Contribute') {
        return {
          name: 'WidgetSignIn',
          params: {
            id,
          },
        };
      }
    }
    return;
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('orders-getOrderByIdWithPlans', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('orders-getOrderByIdWithPlans', { status: 'error', channel: 'merchant' });
    }

    if (userStore.isAuthenticated) {
      window.location.href = `${BASE_URL}/shopper`;
      return;
    }
    return {
      name: 'shopperSignIn',
      query: { redirect: to.fullPath },
    };
  }
};

export const fixStateRedirectGuard = (to: RouteLocationNormalizedGeneric) => {
  const widgetStore = useWidgetStore();

  const id = to.params.id as string;
  if (!widgetStore.id) {
    return {
      name: widgetStore.order.state === 'PARTIAL' ? 'WidgetSignIn' : 'Contribute',
      params: { id },
    };
  }
  return;
};

export const getPaymentMethodRoute = () => {
  const methods = useWidgetStore().order.paymentMethods;
  if (!methods) return { name: 'SelectPaymentMethod' };

  const available = [
    methods.creditOrDebitCard ? { name: 'PeachPayment', query: { paymentMethod: 'CARD' } } : null,
    methods.eft ? { name: 'PeachPayment', query: { paymentMethod: 'EFT' } } : null,
    methods.payAt ? { name: 'PayAt' } : null,
    methods.debiCheck ? { name: 'DebiCheck' } : null,
  ].filter((r): r is NonNullable<typeof r> => r !== null);

  return available.length === 1 ? available[0] : { name: 'SelectPaymentMethod' };
};

export const selectPaymentMethodGuard = (to: RouteLocationNormalizedGeneric) => {
  const id = to.params.id as string;
  const route = getPaymentMethodRoute();
  if (route.name !== 'SelectPaymentMethod') {
    return { ...route, params: { id }, replace: true };
  }
};
